import React, { useState, useEffect } from "react";
import { Drawer, Tabs, Button, Flex, message, Form } from "antd";
import { useDispatch } from "react-redux";
import { parsePhoneNumber } from "react-phone-number-input";
import CustomLoaderIcon from "../../../../../../Theme/CustomIcons/Icons/Essetional/CustomLoaderIcon";

import Basics from "./Tabs/Basics";
import Authentication from "./Tabs/Authentication";
import {
  createUserAction,
  updateUserAction,
  updateUserAuthAction,
  getOneUserDataAction,
  getOneUserAuthDataAction,
} from "../../../../../../store/action/admin/users.action.js";

const AddUserDrawer = ({ open, onClose, userId: propUserId }) => {
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState("basics");
  const [userId, setUserId] = useState(propUserId || null);
  const [loading, setLoading] = useState(false);
  const [stepCompleted, setStepCompleted] = useState({
    basics: false,
    authentication: false,
  });

  const [form] = Form.useForm();

  const tabsOrder = ["basics", "authentication"];

  // --------------------------
  // Reset state when drawer opens or propUserId changes
  // --------------------------
  useEffect(() => {
    setUserId(propUserId || null);
    form.resetFields();
    setStepCompleted({
      basics: !!propUserId,
      authentication: !!propUserId,
    });
    setActiveTab("basics");

    if (propUserId) loadUserData(propUserId);
  }, [propUserId, form]);

  const loadUserData = async (id) => {
    setLoading(true);
    try {
      // Fetch basics
      const res = await dispatch(getOneUserDataAction(id));
      const userData = res?.data?.user || {};
      const extraInfo = res?.data?.extraInfo || {};
      const userEnroll = res?.data?.userEnroll || {};
      const dimensions = res?.data?.dimensions || [];

      const dimensionIds = dimensions.map((d) => d.dimensionId);
      const userRole = res?.data?.dimensions[0].userRole;

      form.setFieldsValue({
        firstName: userData.firstName,
        lastName: userData.lastName,
        displayName: userData.displayName,
        empId: userData.empId,
        personalEmail: userData.personalEmail,
        perPhoneNo: userData.perPhoneNo
          ? `${userData.perCountryCode || ""}${userData.perPhoneNo}`
          : "",
        country: userData.country,
        state: userData.state,
        city: userData.city,
        postalCode: userData.postalCode,
        address: userData.address,
        workEmail: extraInfo.workEmail,
        workMobile: extraInfo.workMobile,
        designationId: extraInfo.designationId,
        entityId: userEnroll?.entityId,
        dimensionIds: dimensionIds?.length ? dimensionIds : [],
        userRole,
      });

      setStepCompleted((prev) => ({ ...prev, basics: true }));

      // Fetch authentication
      const authRes = await dispatch(getOneUserAuthDataAction(id));
      const authData = authRes?.data || {};

      form.setFieldsValue({
        otpEmailEnabled: authData.otpEmailEnabled,
        otpPhoneEnabled: authData.otpPhoneEnabled,
        otpEnabled: authData.otpEmailEnabled || authData.otpPhoneEnabled,
        passwordEnabled: authData.passwordEnabled,
        autoGeneratePassword: authData.autoGeneratePassword,
        requirePasswordChange: authData.requirePasswordChange,
        sendPasswordInEmail: authData.sendPasswordInEmail,
        password: authData.password || "",
        confirmPassword: authData.password || "",
        passwordEmailRecipients: authData.passwordEmailRecipients || "",
      });

      setStepCompleted((prev) => ({ ...prev, authentication: true }));
    } catch (err) {
      message.error("Failed to load user data");
    } finally {
      setLoading(false);
    }
  };

  // --------------------------
  // NEXT / FINISH BUTTON
  // --------------------------
  const handleNext = async () => {
    if (!form) return;

    try {
      const values = await form.validateFields();
      setLoading(true);

      // --------------------------
      // BASICS TAB
      // --------------------------
      if (activeTab === "basics") {
        const personalParsed = values.perPhoneNo
          ? parsePhoneNumber(values.perPhoneNo)
          : null;

        const payload = {
          ...values,
          perCountryCode: personalParsed?.countryCallingCode
            ? `+${personalParsed.countryCallingCode}`
            : "",
          perPhoneNo: personalParsed?.nationalNumber || "",
        };

        let newUserId = userId;
        if (!userId) {
          const res = await dispatch(createUserAction(payload));
          newUserId = res?.data?._id;
          setUserId(newUserId);
        } else {
          await dispatch(updateUserAction(payload, userId));
        }

        // Mark basics as completed
        setStepCompleted((prev) => ({ ...prev, basics: true }));

        // Move to next tab
        const currentIndex = tabsOrder.indexOf(activeTab);
        const nextIndex = currentIndex + 1;
        if (nextIndex < tabsOrder.length) {
          setActiveTab(tabsOrder[nextIndex]);
        }
      }

      // --------------------------
      // AUTHENTICATION TAB
      // --------------------------
      else if (activeTab === "authentication") {
        if (!userId) {
          message.error("User ID missing. Complete Basics first.");
          return;
        }

        // OTP validation
        if (!values.otpEmailEnabled && !values.otpPhoneEnabled) {
          message.error("At least one OTP option must be enabled.");
          return;
        }

        // Password validation
        if (!values.autoGeneratePassword) {
          if (!values.password || !values.confirmPassword) {
            message.error("Password fields are required.");
            return;
          }
          if (values.password !== values.confirmPassword) {
            message.error("Passwords do not match.");
            return;
          }
        }

        if (values.autoGeneratePassword && !values.passwordEmailRecipients) {
          message.error(
            "Email recipients are required when sending password via email."
          );
          return;
        }

        const authPayload = {
          otpEnabled: values.otpEnabled || false,
          otpEmailEnabled: values.otpEmailEnabled || false,
          otpPhoneEnabled: values.otpPhoneEnabled || false,
          passwordEnabled: values.passwordEnabled || false,
          autoGeneratePassword: values.autoGeneratePassword || false,
          requirePasswordChange: values.requirePasswordChange || false,
          sendPasswordInEmail: values.sendPasswordInEmail || false,
          password: values.password || "",
          email: values.passwordEmailRecipients || "",
        };

        await dispatch(updateUserAuthAction(authPayload, userId));

        message.success("User saved successfully!");

        // Reset everything and close drawer
        handleCancel();
      }
    } catch (err) {
      if (err?.errorFields) {
        err.errorFields.forEach((field) =>
          message.error(`${field.name[0]}: ${field.errors.join(", ")}`)
        );
      } else if (typeof err === "string") {
        message.error(err);
      } else {
        message.error("Something went wrong. Please check inputs.");
      }
    } finally {
      setLoading(false);
    }
  };

  // --------------------------
  // CANCEL / CLOSE DRAWER
  // --------------------------
  const handleCancel = () => {
    form.resetFields();
    setActiveTab("basics");
    setStepCompleted({ basics: false, authentication: false });
    onClose(); // only close here, don’t reset userId yet
  };

  const tabItems = [
    {
      key: "basics",
      label: "Basics",
      children: <Basics userId={userId} form={form} />,
    },
    {
      key: "authentication",
      label: "Authentication",
      disabled: !stepCompleted.basics,
      children: <Authentication userId={userId} form={form} />,
    },
  ];

  return (
    <Drawer
      title={userId ? "Edit User" : "Add a User"}
      placement="right"
      open={open}
      width={1318}
      destroyOnClose
      onClose={handleCancel}
      afterVisibleChange={(visible) => {
        if (!visible) {
          // Drawer has fully closed → safe to reset userId
          setUserId(null);
          form.resetFields();
          setStepCompleted({ basics: false, authentication: false });
          setActiveTab("basics");
        }
      }}
      footer={
        <Flex justify="flex-end" gap={8}>
          {activeTab === "basics" ? (
            <Button onClick={handleCancel}>Cancel</Button>
          ) : (
            <Button
              onClick={() =>
                setActiveTab(tabsOrder[tabsOrder.indexOf(activeTab) - 1])
              }>
              Previous
            </Button>
          )}
          <Button type="primary" loading={loading} onClick={handleNext}>
            {activeTab !== "authentication" ? "Next" : "Finish"}
          </Button>
        </Flex>
      }>
      {loading ? (
        <div
          style={{
            height: "60vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontSize: 18,
            fontWeight: 500,
          }}>
          <CustomLoaderIcon />
        </div>
      ) : (
        <Tabs
          activeKey={activeTab}
          items={tabItems}
          onChange={(key) => {
            const tabIndex = tabsOrder.indexOf(key);
            if (
              key === "basics" ||
              (tabIndex > 0 && stepCompleted[tabsOrder[tabIndex - 1]])
            ) {
              setActiveTab(key);
            }
          }}
        />
      )}
    </Drawer>
  );
};

export default AddUserDrawer;
